import { type LucideIcon, TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";

export interface StatCardProps {
  label: string;
  value: number | string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  trend?: number;
  trendLabel?: string;
  formatValue?: (v: number | string) => string;
  /** Optional compact formatter for mobile view (e.g., "565 tkr" instead of "565 460 kr") */
  formatValueCompact?: (v: number | string) => string;
  href?: string;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  iconBg,
  iconColor,
  trend,
  trendLabel = "vs föregående period",
  formatValue,
  formatValueCompact,
  href,
}: StatCardProps) {
  const displayValue = formatValue ? formatValue(value) : String(value);
  const displayValueCompact = formatValueCompact ? formatValueCompact(value) : displayValue;

  // Always use compact format when available, for cleaner display of large numbers
  const primaryDisplay = formatValueCompact ? displayValueCompact : displayValue;

  const content = (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-600">{label}</p>
        {/* Always show compact format for cleaner display of large currency values */}
        <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 leading-tight truncate" title={displayValue}>
          {primaryDisplay}
        </p>
        {trend !== undefined && (
          <div className="flex items-center gap-1.5 mt-2">
            {trend >= 0 ? (
              <TrendingUp size={14} className="text-emerald-600" />
            ) : (
              <TrendingDown size={14} className="text-red-500" />
            )}
            <span
              className={`text-sm font-medium ${
                trend >= 0 ? "text-emerald-600" : "text-red-500"
              }`}
            >
              {trend >= 0 ? "+" : ""}
              {trend.toLocaleString("sv-SE", { maximumFractionDigits: 1 })}%
            </span>
            <span className="text-xs text-gray-400">{trendLabel}</span>
          </div>
        )}
      </div>
      <div
        className={`w-10 h-10 sm:w-12 sm:h-12 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}
      >
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="card group hover:shadow-md hover:border-primary-200 transition-all"
      >
        {content}
      </Link>
    );
  }

  return <div className="card">{content}</div>;
}
